import React, { useMemo } from 'react';
import { Box, Typography, Card, CardContent, Grid, Divider } from '@mui/material';
import { useReportConfig } from '../../contexts/ReportConfigContext.jsx';
import ReportStyledField from './ReportStyledField.jsx';
import ReportWidget from './ReportWidget';

const safeNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const formatArea = (value) => {
  const n = safeNumber(value);
  if (n === null) return '-';
  return `${n.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ha`;
};

const formatPercent = (value) => {
  const n = safeNumber(value);
  if (n === null) return '-';
  return `${n.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} %`;
};

const formatGeneric = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  return String(value);
};

const normalizeSncrData = (sncrRaw) => {
  if (!sncrRaw) return [];

  if (Array.isArray(sncrRaw)) {
    return sncrRaw.filter((item) => item && typeof item === 'object');
  }

  if (sncrRaw.details && Array.isArray(sncrRaw.details)) {
    return sncrRaw.details.filter((item) => item && typeof item === 'object');
  }

  if (typeof sncrRaw === 'object') {
    return [sncrRaw];
  }

  return [];
};

function SncrTitularPrintCard({ titular }) {
  const { isFieldEnabled, getFieldLabel } = useReportConfig();

  if (!titular || typeof titular !== 'object') return null;

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2,
        bgcolor: 'grey.50',
      }}
    >
      <CardContent>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {formatGeneric(titular.titular) || 'Titular'}
        </Typography>
        <Grid container spacing={1}>
          {isFieldEnabled('sncr', 'titulares', 'cpf_cnpj') && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {getFieldLabel('sncr', 'cpf_cnpj', 'CPF/CNPJ')}
              </Typography>
              <ReportStyledField
                domainKey="sncr"
                fieldKey="cpf_cnpj"
                variant="body2"
                defaultSx={{ fontWeight: 500 }}
              >
                {formatGeneric(titular.cpf_cnpj)}
              </ReportStyledField>
            </Grid>
          )}
          {isFieldEnabled('sncr', 'titulares', 'percentual_de_detencao') && (
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {getFieldLabel('sncr', 'percentual_de_detencao', 'Percentual')}
              </Typography>
              <ReportStyledField
                domainKey="sncr"
                fieldKey="percentual_de_detencao"
                variant="body2"
                defaultSx={{ fontWeight: 500 }}
              >
                {formatPercent(titular['percentual_de_detenção'] || titular.percentual_de_detenção)}
              </ReportStyledField>
            </Grid>
          )}
          {isFieldEnabled('sncr', 'titulares', 'tipo_de_proprietario') && (
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {getFieldLabel('sncr', 'tipo_de_proprietario', 'Tipo')}
              </Typography>
              <ReportStyledField
                domainKey="sncr"
                fieldKey="tipo_de_proprietario"
                variant="body2"
                defaultSx={{ fontWeight: 500 }}
              >
                {formatGeneric(titular['tipo_de_proprietário'] || titular.tipo_de_proprietario)}
              </ReportStyledField>
            </Grid>
          )}
          {isFieldEnabled('sncr', 'titulares', 'natureza_juridica') && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {getFieldLabel('sncr', 'natureza_juridica', 'Natureza Jurídica')}
              </Typography>
              <ReportStyledField
                domainKey="sncr"
                fieldKey="natureza_juridica"
                variant="body2"
                defaultSx={{ fontWeight: 500 }}
              >
                {formatGeneric(titular['natureza_jurídica'] || titular.natureza_juridica)}
              </ReportStyledField>
            </Grid>
          )}
          {isFieldEnabled('sncr', 'titulares', 'pais_de_origem') &&
          (titular['país_de_origem'] || titular.pais_de_origem) ? (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {getFieldLabel('sncr', 'pais_de_origem', 'País de Origem')}
              </Typography>
              <ReportStyledField
                domainKey="sncr"
                fieldKey="pais_de_origem"
                variant="body2"
                defaultSx={{ fontWeight: 500 }}
              >
                {formatGeneric(titular['país_de_origem'] || titular.pais_de_origem)}
              </ReportStyledField>
            </Grid>
          ) : null}
        </Grid>
      </CardContent>
    </Card>
  );
}

export default function ReportPrintSncrDetails({ snapshot }) {
  const { isDomainEnabled, isSectionEnabled, isFieldEnabled, getFieldLabel } = useReportConfig();

  if (!isDomainEnabled('sncr')) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Domínio SNCR desabilitado na configuração do relatório.
        </Typography>
      </Box>
    );
  }

  const properties =
    snapshot && snapshot.properties && typeof snapshot.properties === 'object'
      ? snapshot.properties
      : {};

  const sncrRaw = properties.sncr;
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const records = useMemo(() => normalizeSncrData(sncrRaw), [sncrRaw]);

  if (!records.length) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Nenhum registro SNCR encontrado para este relatório.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        SNCR - Sistema Nacional de Cadastro Rural
      </Typography>

      {records.map((sncr, index) => {
        const titulares = Array.isArray(sncr.titulares) ? sncr.titulares : [];

        return (
          <Card
            key={sncr.codigo_incra || index}
            sx={{
              mb: 2,
              borderRadius: 2,
              borderLeft: 4,
              borderLeftColor: 'primary.main',
              boxShadow: 1,
            }}
          >
            <CardContent>
              {isSectionEnabled('sncr', 'dados_imovel') && (
                <ReportWidget sectionKey="sncr" widgetKey="sncr_dados_imovel">
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Informações do Imóvel
                  </Typography>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    {isFieldEnabled('sncr', 'dados_imovel', 'codigo_incra') && (
                      <Grid item xs={12} md={4}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', fontWeight: 600 }}
                        >
                          {getFieldLabel('sncr', 'codigo_incra', 'Código do Imóvel')}
                        </Typography>
                        <ReportStyledField
                          domainKey="sncr"
                          fieldKey="codigo_incra"
                          variant="body2"
                          defaultSx={{ fontWeight: 500 }}
                        >
                          {formatGeneric(sncr.codigo_incra)}
                        </ReportStyledField>
                      </Grid>
                    )}

                    {isFieldEnabled('sncr', 'dados_imovel', 'nome_da_propriedade') && (
                      <Grid item xs={12} md={4}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', fontWeight: 600 }}
                        >
                          {getFieldLabel('sncr', 'nome_da_propriedade', 'Nome do Imóvel')}
                        </Typography>
                        <ReportStyledField
                          domainKey="sncr"
                          fieldKey="nome_da_propriedade"
                          variant="body2"
                          defaultSx={{ fontWeight: 500 }}
                        >
                          {formatGeneric(sncr.nome_da_propriedade)}
                        </ReportStyledField>
                      </Grid>
                    )}

                    {isFieldEnabled('sncr', 'dados_imovel', 'uf') && (
                      <Grid item xs={6} md={2}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', fontWeight: 600 }}
                        >
                          {getFieldLabel('sncr', 'uf', 'UF')}
                        </Typography>
                        <ReportStyledField
                          domainKey="sncr"
                          fieldKey="uf"
                          variant="body2"
                          defaultSx={{ fontWeight: 500 }}
                        >
                          {formatGeneric(sncr.uf)}
                        </ReportStyledField>
                      </Grid>
                    )}

                    {isFieldEnabled('sncr', 'dados_imovel', 'municipio') && (
                      <Grid item xs={12} md={4}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', fontWeight: 600 }}
                        >
                          {getFieldLabel('sncr', 'municipio', 'Município')}
                        </Typography>
                        <ReportStyledField
                          domainKey="sncr"
                          fieldKey="municipio"
                          variant="body2"
                          defaultSx={{ fontWeight: 500 }}
                        >
                          {formatGeneric(sncr['município'] || sncr.municipio)}
                        </ReportStyledField>
                      </Grid>
                    )}

                    {isFieldEnabled('sncr', 'dados_imovel', 'area_da_propriedade') && (
                      <Grid item xs={6} md={4}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', fontWeight: 600 }}
                        >
                          {getFieldLabel('sncr', 'area_da_propriedade', 'Área Total')}
                        </Typography>
                        <ReportStyledField
                          domainKey="sncr"
                          fieldKey="area_da_propriedade"
                          variant="body2"
                          defaultSx={{ fontWeight: 500 }}
                        >
                          {formatArea(sncr['área_da_propriedade'] || sncr.area_da_propriedade)}
                        </ReportStyledField>
                      </Grid>
                    )}
                  </Grid>
                </ReportWidget>
              )}

              {titulares.length > 0 && isSectionEnabled('sncr', 'titulares') && (
                <ReportWidget sectionKey="sncr" widgetKey="sncr_titulares">
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {getFieldLabel('sncr', 'titulares', `Titulares (${titulares.length})`)}
                  </Typography>
                  <Grid container spacing={2}>
                    {titulares.map((titular, tIndex) => (
                      <Grid item xs={12} md={6} key={tIndex}>
                        <SncrTitularPrintCard titular={titular} />
                      </Grid>
                    ))}
                  </Grid>
                </ReportWidget>
              )}
            </CardContent>
          </Card>
        );
      })}
    </Box>
  );
}
